import type { VatMode } from '@crm/shared'
import { generateKP } from './generateKP'
import { generateKpExcelMosel } from './generateKpExcelMosel'
import type { KpPdfItem } from './generateKpPdf'
import { getKpEntity } from './kpEntities'

export type KpFormat = 'pdf' | 'excel'

export interface KpDownloadPayload {
  requestNumber?: string
  clientName?: string | null
  /** Кто выставляет КП (вошедший пользователь / автор снапшота). */
  responsibleName?: string | null
  /** Телефон ответственного (из профиля). Пусто → телефон по умолчанию. */
  responsiblePhone?: string | null
  /** Личная почта ответственного (из профиля) — строка «Почта» под телефоном. */
  responsibleEmail?: string | null
  /** Кому адресовано КП (необязательно) — только в PDF. */
  recipient?: string | null
  /** id юрлица из KP_ENTITIES: по умолчанию — юрлицо продажи заявки. */
  entityId: string
  /** Режим НДС КП: «С НДС» (по умолчанию) или «НДС сверху». */
  vatMode?: VatMode
  items: KpPdfItem[]
}

/**
 * PDF-генератор — отдельный файл сайта, браузер догружает его при скачивании.
 * Если вкладка открыта до выката новой версии, старого файла на сервере уже
 * нет и загрузка падает с английским «Failed to fetch dynamically imported
 * module» — пользователь видел только «Не удалось сформировать файл КП» (14.10).
 * Говорим по-русски, что делать.
 */
export async function loadPdfModule<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load()
  } catch {
    throw new Error(
      'Не удалось загрузить модуль PDF — вероятно, сайт обновился. Обновите страницу (Ctrl+F5) и скачайте КП ещё раз.',
    )
  }
}

/**
 * Единая точка скачивания КП в выбранном формате. Excel-генератор (generateKP)
 * оставлен без изменений — он уже печатает блок «Исполнитель/Ответственный»,
 * туда передаём имя ответственного. PDF-генератор повторяет тот же шаблон.
 * У МОСЭЛ свой бланк (layout 'mosel') — свои генераторы PDF и Excel, содержание
 * то же: итоги, условия, исполнитель (решение 14.10.2026).
 */
export async function downloadKp(format: KpFormat, payload: KpDownloadPayload): Promise<void> {
  const entity = getKpEntity(payload.entityId)
  if (entity.layout === 'mosel') {
    const common = {
      entity,
      requestNumber: payload.requestNumber,
      clientName: payload.clientName ?? null,
      responsibleName: payload.responsibleName ?? null,
      responsiblePhone: payload.responsiblePhone ?? null,
      responsibleEmail: payload.responsibleEmail ?? null,
      vatMode: payload.vatMode,
      items: payload.items,
    }
    if (format === 'excel') {
      await generateKpExcelMosel(common)
      return
    }
    const { generateKpPdfMosel } = await loadPdfModule(() => import('./generateKpPdfMosel'))
    await generateKpPdfMosel({ ...common, recipient: payload.recipient ?? null })
    return
  }
  if (format === 'excel') {
    await generateKP({
      requestNumber: payload.requestNumber,
      clientName: payload.clientName ?? null,
      managerName: payload.responsibleName ?? null,
      managerPhone: payload.responsiblePhone ?? null,
      managerEmail: payload.responsibleEmail ?? null,
      entity,
      vatMode: payload.vatMode,
      items: payload.items,
    })
    return
  }
  // Заглушка: для юрлиц без готового PDF-шаблона (напр. ДИСТИНКТ) PDF недоступен.
  // UI блокирует кнопку, но на всякий случай защищаемся и здесь.
  if (!entity.pdfEnabled) {
    throw new Error(`PDF для «${entity.shortName}» пока недоступен — скачайте КП в Excel.`)
  }
  // jsPDF и шрифт-сабсеты тяжёлые — грузим PDF-генератор динамически, только
  // когда пользователь реально выбрал формат PDF (отдельный чанк).
  const { generateKpPdf } = await loadPdfModule(() => import('./generateKpPdf'))
  await generateKpPdf({
    entity,
    requestNumber: payload.requestNumber,
    clientName: payload.clientName ?? null,
    responsibleName: payload.responsibleName ?? null,
    responsiblePhone: payload.responsiblePhone ?? null,
    responsibleEmail: payload.responsibleEmail ?? null,
    recipient: payload.recipient ?? null,
    vatMode: payload.vatMode,
    items: payload.items,
  })
}
